<template>
  <div class="setup-wizard-page">
    <v-container class="setup-wizard-content">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <div class="setup-wizard-card" flat>
            <v-card-title
              class="text-h5 font-weight-bold justify-center text-center py-6 setup-header"
            >
              <v-icon x-large class="mr-3" color="primary">mdi-cog-outline</v-icon>
              Setting up your organization
            </v-card-title>

            <v-card-text>
              <div class="text-center">
                <!-- Loader Spinner -->
                <div class="loader-container">
                  <div v-if="!setupCompleted && !setupFailed" class="loader-wrapper">
                    <LoaderComponent :size="20" class="main-loader" />
                    <!-- <span class="text-h6 font-weight-bold loader-text">
                      {{ currentStep + 1 }}/{{ totalSteps }}
                    </span> -->
                  </div>

                  <!-- Success Icon -->
                  <v-icon
                    v-else-if="setupCompleted"
                    size="120"
                    color="success"
                    class="success-icon"
                  >
                    mdi-check-circle-outline
                  </v-icon>

                  <!-- Error Icon -->
                  <v-icon v-else-if="setupFailed" size="100" color="error" class="error-icon">
                    mdi-close-circle-outline
                  </v-icon>
                </div>

                <!-- Current Step Message -->
                <div class="current-step-message mt-8">
                  <h3 class="text-h6 font-weight-medium">{{ currentStepTitle }}</h3>
                  <p class="text-body-1 mt-2">{{ currentStepMessage }}</p>

                  <!-- Completion Message -->
                  <div v-if="setupCompleted" class="mt-4">
                    <p class="text-body-1 success--text">Setup completed successfully!</p>
                    <p class="text-body-2">Redirecting to dashboard...</p>
                  </div>

                  <!-- Error Message -->
                  <div v-else-if="setupFailed" class="mt-4">
                    <p class="text-body-1 error--text">Setup failed. Please try again.</p>
                  </div>
                </div>
              </div>
            </v-card-text>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import {
  adminRolePayload,
  useCasePayload,
  extendUseCasePayload,
  attendanceSetupPayload,
  taskSetupPayload,
  empAppSetupPayload,
  adminDashboardSetupPayload
} from '@/utils/seedPayloadData'
import { dynamicRequestPath } from '@/utils/workforce/constants'
import LoaderComponent from '@/components/UI/Loader.vue'

export default {
  name: 'SetupWizard',
  components: {
    LoaderComponent
  },
  data() {
    return {
      // Removed showWizard as it's not needed for a page component
      currentStep: 0,
      setupCompleted: false,
      setupFailed: false,
      totalSteps: 10,
      progressValue: 0,
      currentStepTitle: 'Getting Started',
      currentStepMessage: 'Initializing setup process...',
      steps: [
        {
          id: 1,
          title: 'Employee Onboarding',
          status: 'pending',
          message: 'Refreshing employee data...'
        },
        {
          id: 2,
          title: 'Creating Admin Role',
          status: 'pending',
          message: 'Setting up administrator permissions...'
        },
        {
          id: 3,
          title: 'Assigning Role',
          status: 'pending',
          message: 'Assigning admin role to user...'
        },
        {
          id: 4,
          title: 'Creating Branch & Team',
          status: 'pending',
          message: 'Setting up organizational structure...'
        },
        {
          id: 5,
          title: 'Assigning Branch & Team',
          status: 'pending',
          message: 'Linking user to branch and team...'
        },
        {
          id: 6,
          title: 'Use Case Mapping',
          status: 'pending',
          message: 'Configuring use case mappings...'
        },
        {
          id: 7,
          title: 'Attendance Settings',
          status: 'pending',
          message: 'Setting default attendance configurations...'
        },
        {
          id: 8,
          title: 'Task Step Settings',
          status: 'pending',
          message: 'Configuring task workflow defaults...'
        },
        {
          id: 9,
          title: 'Employee app Settings Setup',
          status: 'pending',
          message: 'Configuring employee app default settings...'
        },
        {
          id: 10,
          title: 'Admin dashboard screen Setup',
          status: 'pending',
          message: 'Configuring admin dashboard screen settings...'
        }
      ],
      completedSteps: new Set(), // To track which steps have been completed
      orgId: null,
      orgCode: null,
      checklistData: null // To store the checklist data from backend
    }
  },
  computed: {
    visibleSteps() {
      return this.steps.slice(0, Math.min(this.currentStep + 2, this.steps.length))
    }
  },
  watch: {
    // Removed the value watcher as it's not needed for a page component
    currentStep() {
      this.animateProgress()
    }
  },
  mounted() {
    // Start the setup process when the component is mounted
    this.startSetup()
  },
  methods: {
    animateProgress() {
      const targetValue = (this.currentStep / this.totalSteps) * 100
      const increment = targetValue / 20 // Animation steps
      let currentValue = this.progressValue

      const timer = setInterval(() => {
        if (currentValue < targetValue) {
          currentValue += increment
          this.progressValue = Math.min(currentValue, targetValue)
        } else {
          this.progressValue = targetValue
          clearInterval(timer)
        }
      }, 50)
    },

    // Method to get organization data to extract orgId and orgCode
    async getOrgData() {
      try {
        const orgData = JSON.parse(localStorage.getItem('orgData')) || {}
        this.orgId = orgData?.id || null
        this.orgCode = orgData?.code || null
      } catch (error) {
        console.error('Failed to get organization data', error)
      }
    },

    // Method to check if a step has been completed using backend API
    async isStepCompleted(stepIndex) {
      try {
        // First get org data if not already available
        if (!this.orgId || !this.orgCode) {
          await this.getOrgData()
        }

        // If still no orgId, we can't check status
        if (!this.orgId) {
          return false
        }

        // Call the backend checklist API to get the entire checklist for this org
        const response = await this.$axios.get(`/orgSetup/org-onboarding-checklist/${this.orgId}`)
        console.log('orgId', this.orgId, this.orgCode, response)
        this.checklistData = response.data || {}

        // Map step indices to field names in the checklist
        const stepFields = [
          'employeeOnboarding',
          'adminRoleCreation',
          'roleAssignment',
          'branchTeamCreation',
          'branchTeamAssignment',
          'useCaseMapping',
          'attendanceSettings',
          'taskSettings',
          'employeeAppSettings',
          'adminDashboardSettings'
        ]

        const stepField = stepFields[stepIndex]
        // Access the completed property of the step object
        return this.checklistData[stepField]?.completed || false
      } catch (error) {
        console.error(`Failed to check completion status for step ${stepIndex}`, error)
        this.checklistData = {}
        return false
      }
    },

    // Method to update the entire checklist in the backend
    async updateChecklist(stepIndex) {
      try {
        // First get org data if not already available
        if (!this.orgId || !this.orgCode) {
          await this.getOrgData()
        }

        // If still no orgId or orgCode, we can't update
        if (!this.orgId || !this.orgCode) {
          return false
        }

        // Initialize checklist data if not already present
        if (!this.checklistData) {
          this.checklistData = {}
        }

        // Map step indices to field names in the checklist
        const stepFields = [
          'employeeOnboarding',
          'adminRoleCreation',
          'roleAssignment',
          'branchTeamCreation',
          'branchTeamAssignment',
          'useCaseMapping',
          'attendanceSettings',
          'taskSettings',
          'employeeAppSettings',
          'adminDashboardSettings'
        ]

        const stepField = stepFields[stepIndex]
        // Update the step object with completed: true
        this.checklistData[stepField] = { completed: true }

        // Prepare payload
        const payload = {
          orgId: Number(this.orgId),
          orgCode: this.orgCode,
          ...this.checklistData
        }

        // Update the backend with the entire checklist
        await this.$axios.post(`/orgSetup/org-onboarding-checklist`, payload)

        return true
      } catch (error) {
        console.error(`Failed to update checklist for step ${stepIndex}`, error)
        // We don't want to fail the whole process if we can't update the status
        return false
      }
    },

    async startSetup() {
      this.setupCompleted = false
      this.setupFailed = false
      this.currentStep = 0
      this.progressValue = 0
      this.currentStepTitle = 'Getting Started'
      this.currentStepMessage = 'Initializing setup process...'
      let roleId = null
      let user = null
      let branchId = null
      let teamId = null
      const useCase = JSON.parse(localStorage.getItem('setupUseCase')) || 'collection_management'

      try {
        // Get organization data
        await this.getOrgData()

        // Step 1: Refresh employee data (skip if already done)
        const isStep1Completed = await this.isStepCompleted(0)
        console.log('Step 1 completed:', isStep1Completed)
        if (!isStep1Completed) {
          this.currentStepTitle = 'Employee Onboarding'
          this.currentStepMessage = 'Refreshing employee data...'
          await this.executeStep(0, async () => {
            await this.delay(1500)
            await this.$axios.get('/import/users')
            const userResponse = await this.$axios.get(
              '/v2/users?limit=*&sortBy=firstName&sortOrder=asc'
            )
            user = userResponse?.data?.users[0] || null
            this.completedSteps.add(0)
            // Update checklist in backend
            await this.updateChecklist(0)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 1: Employee Onboarding (already completed)')
          this.completedSteps.add(0)
        }
        try {
          const rolesResponse = await this.$axios.get('/org/role/v2?search=Admin')
          if (rolesResponse.data?.roles?.length > 0) {
            roleId = rolesResponse.data.roles[0]._id
          }
        } catch (e) {
          console.log('Could not fetch existing admin role')
        }
        // Step 2: Create admin role (skip if already done)
        const isStep2Completed = await this.isStepCompleted(1)
        if (!isStep2Completed && !roleId) {
          this.currentStepTitle = 'Creating Admin Role'
          this.currentStepMessage = 'Setting up administrator permissions...'
          await this.executeStep(1, async () => {
            await this.delay(1500)
            const payload = { ...adminRolePayload }
            const roleResponse = await this.$axios.post('/org/role/v2', payload)
            roleId = roleResponse.data?.role?._id
            this.completedSteps.add(1)
            // Update checklist in backend
            await this.updateChecklist(1)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 2: Creating Admin Role (already completed)')
          this.completedSteps.add(1)
          // We still need the roleId for later steps, so try to fetch it
        }

        // Step 3: Assign role to employee (skip if already done)
        const isStep3Completed = await this.isStepCompleted(2)
        if (!isStep3Completed) {
          // Make sure we have user and roleId before proceeding
          if (!user) {
            const userResponse = await this.$axios.get(
              '/v2/users?limit=*&sortBy=firstName&sortOrder=asc'
            )
            user = userResponse?.data?.users[0] || null
          }

          if (user && roleId) {
            this.currentStepTitle = 'Assigning Role'
            this.currentStepMessage = 'Assigning admin role to user...'
            await this.executeStep(2, async () => {
              await this.delay(1500)
              await this.$axios.patch(`/users/${user?._id}`, {
                wfmRoleId: roleId
              })
              this.completedSteps.add(2)
              // Update checklist in backend
              await this.updateChecklist(2)
              return Promise.resolve()
            })
          } else {
            console.log('Skipping Step 3: Assigning Role (missing user or role data)')
            this.completedSteps.add(2)
            // Update checklist in backend even if we couldn't execute it fully
            await this.updateChecklist(2)
          }
        } else {
          console.log('Skipping Step 3: Assigning Role (already completed)')
          this.completedSteps.add(2)
        }

        // Step 4: Create branch and team (skip if already done)
        const isStep4Completed = await this.isStepCompleted(3)
        if (!isStep4Completed) {
          this.currentStepTitle = 'Creating Branch & Team'
          this.currentStepMessage = 'Setting up organizational structure...'

          await this.executeStep(3, async () => {
            await this.delay(1500)
            // Create branch
            const branchPayload = [
              {
                branchName: 'Main',
                branchCode: 'main',
                branchDescription: 'Default branch created by cualitywork'
              }
            ]
            const branchResponse = await this.$axios.post('/branch', branchPayload)
            branchId = branchResponse.data.branch[0]._id

            // Create team
            const teamPayload = [
              {
                teamName: 'Team A',
                teamCode: 'teamA',
                teamDescription: 'Default team created by cualitywork',
                branch: branchId
              }
            ]
            const teamResponse = await this.$axios.post('/team', teamPayload)
            teamId = teamResponse.data.team[0]._id

            this.completedSteps.add(3)
            // Update checklist in backend
            await this.updateChecklist(3)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 4: Creating Branch & Team (already completed)')
          this.completedSteps.add(3)
        }

        // Step 5: Assign branch and team to employee (skip if already done)
        const isStep5Completed = await this.isStepCompleted(4)
        if (!isStep5Completed) {
          this.currentStepTitle = 'Assigning Branch & Team'
          this.currentStepMessage = 'Linking user to branch and team...'
          await this.executeStep(4, async () => {
            await this.delay(1500)
            // We would need to fetch the user, branch, and team IDs if not already available
            const userResponse = await this.$axios.get(
              '/v2/users?limit=*&sortBy=firstName&sortOrder=asc'
            )
            const users = userResponse?.data?.users || []
            if (users.length > 0) {
              user = users[0]

              // Get branch info
              const branchResponse = await this.$axios.get('/branches')
              const branches = branchResponse?.data?.branches || []
              if (branches.length > 0) {
                const branchId = branches[0]._id

                const userInfo = {
                  branch: branchId,
                  team: teamId || undefined
                }
                await this.$axios.patch(`/users/${user._id}?action=UPDATE_USER_INFO`, {
                  userInfo
                })
              }
            }
            this.completedSteps.add(4)
            // Update checklist in backend
            await this.updateChecklist(4)
          })
        } else {
          console.log('Skipping Step 5: Assigning Branch & Team (already completed)')
          this.completedSteps.add(4)
        }

        // Step 6: Create use case mapping (skip if already done)
        const isStep6Completed = await this.isStepCompleted(5)
        if (!isStep6Completed) {
          this.currentStepTitle = 'Use Case Mapping'
          this.currentStepMessage = 'Configuring use case mappings...'
          await this.executeStep(5, async () => {
            await this.delay(1500)
            //create mapping
            const mappingPayload = { ...useCasePayload[useCase] }
            const res = await this.$axios.post(
              `/orgSetup/${dynamicRequestPath[useCase]?.path}`,
              mappingPayload
            )
            const payload = {
              ...extendUseCasePayload,
              useCase,
              name: useCase === 'collection_management' ? 'Collection New' : 'Loan New',
              [dynamicRequestPath[useCase]?.key]: res.data?.data?._id
            }
            await this.$axios.post('/orgSetup/extend-usecase', payload)
            this.completedSteps.add(5)
            // Update checklist in backend
            await this.updateChecklist(5)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 6: Use Case Mapping (already completed)')
          this.completedSteps.add(5)
        }

        // Step 7: Set attendance default settings (skip if already done)
        const isStep7Completed = await this.isStepCompleted(6)
        if (!isStep7Completed) {
          this.currentStepTitle = 'Attendance Settings'
          this.currentStepMessage = 'Setting default attendance configurations...'
          await this.executeStep(6, async () => {
            await this.delay(1500)
            await this.$axios.post(`/orgSetup/attendance-settings`, {
              ...attendanceSetupPayload
            })
            this.completedSteps.add(6)
            // Update checklist in backend
            await this.updateChecklist(6)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 7: Attendance Settings (already completed)')
          this.completedSteps.add(6)
        }

        // Step 8: Set task step default settings (skip if already done)
        const isStep8Completed = await this.isStepCompleted(7)
        if (!isStep8Completed) {
          this.currentStepTitle = 'Task Step Settings'
          this.currentStepMessage = 'Configuring task workflow defaults...'
          await this.executeStep(7, async () => {
            await this.delay(1500)
            await this.$axios.post('/orgSetup/task-settings', {
              ...taskSetupPayload
            })
            this.completedSteps.add(7)
            // Update checklist in backend
            await this.updateChecklist(7)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 8: Task Step Settings (already completed)')
          this.completedSteps.add(7)
        }

        // Step 9: Employee app settings (skip if already done)
        const isStep9Completed = await this.isStepCompleted(8)
        if (!isStep9Completed) {
          this.currentStepTitle = 'Employee app Settings Setup'
          this.currentStepMessage = 'Configuring employee app default settings....'
          await this.executeStep(8, async () => {
            await this.delay(1500)
            await this.$axios.post('/orgSetup/empapp-settings', {
              ...empAppSetupPayload
            })
            this.completedSteps.add(8)
            // Update checklist in backend
            await this.updateChecklist(8)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 9: Employee app Settings (already completed)')
          this.completedSteps.add(8)
        }

        // Step 10: Admin dashboard settings (skip if already done)
        const isStep10Completed = await this.isStepCompleted(9)
        if (!isStep10Completed) {
          this.currentStepTitle = 'Admin dashboard screen Setup'
          this.currentStepMessage = 'Configuring admin dashboard screen settings...'
          await this.executeStep(9, async () => {
            await this.delay(1500)
            await this.$axios.post('/orgSetup/admin-dashboard-settings', {
              ...adminDashboardSetupPayload
            })
            this.completedSteps.add(9)
            // Update checklist in backend
            await this.updateChecklist(9)
            return Promise.resolve()
          })
        } else {
          console.log('Skipping Step 10: Admin dashboard Settings (already completed)')
          this.completedSteps.add(9)
        }

        // All steps completed successfully
        this.setupCompleted = true
        this.currentStepTitle = 'Setup Complete'
        this.currentStepMessage = 'Organization setup completed successfully!'

        // Emit event for parent components if needed
        this.$emit('setup-completed')

        // Redirect to dashboard after a delay
        setTimeout(() => {
          this.$router.push('/workforce/dashboard')
        }, 2000)
      } catch (error) {
        console.error('Setup failed at step', this.currentStep, error)
        this.setupFailed = true
        this.currentStepTitle = 'Setup Failed'
        this.currentStepMessage = 'Failed to complete setup. Please try again.'
        this.$emit('setup-failed', error)
      }
    },

    async executeStep(stepIndex, action) {
      // Move to the current step
      this.currentStep = stepIndex

      // Execute the action directly without unnecessary try/catch
      await action()
    },

    handleClose() {
      // For a page component, we redirect to the dashboard
      this.$router.push('/workforce/dashboard')
    },

    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    }
  }
}
</script>

<style scoped>
.setup-wizard-page {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
}

.setup-wizard-content {
  width: 100%;
  padding: 20px;
}

.setup-wizard-card {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  /* box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2); */
}

.setup-header {
  background-color: #f5f5f5;
}

.loader-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  /* margin-bottom: 24px; */
}

.loader-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.main-loader {
  margin-bottom: 16px;
}

.loader-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.success-icon,
.error-icon {
  animation: pop-in 0.5s ease-out;
}

@keyframes pop-in {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.current-step-message {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
}
</style>
